/**
 * RP4 — Outer-stage callback wiring helpers.
 *
 * The `<GripsDomLayer>` props mix two optional callbacks
 * (`onLookupGripClick`, `onGripContextMenu`) that need lightweight
 * adapters between the grip overlay's `Grip` shape and the host
 * callbacks' (entityId / parameterId / screen) shape. Hoisting them
 * keeps the JSX render path readable.
 */

import type { Grip } from "../grips/computeGrips";
import type { Scene3DStageProps } from "../Scene3DStageProps";

/**
 * Build the lookup-grip click adapter, returning `undefined` when the
 * host did not pass `onBlockLookupGripClick` (so the prop is omitted
 * from the GripsDomLayer JSX rather than wired to a no-op).
 */
export function buildLookupGripClickAdapter(
  cb: Scene3DStageProps["onBlockLookupGripClick"],
):
  | ((grip: Grip, screen: { x: number; y: number }) => void)
  | undefined {
  if (!cb) return undefined;
  return (grip, screen) => {
    const param = (
      grip.payload as { parameterId?: string } | undefined
    )?.parameterId;
    if (!param) return;
    cb({
      entityId: grip.entityId,
      parameterId: param,
      screen,
    });
  };
}

/**
 * Build the mleader grip context-menu adapter. Returns `undefined`
 * when the host did not pass the callback. Filters out non-mleader
 * grips so the browser-default context menu still fires for them.
 */
export function buildMleaderGripContextMenuAdapter(
  cb: Scene3DStageProps["onMleaderGripContextMenu"],
):
  | ((grip: Grip, screen: { x: number; y: number }) => void)
  | undefined {
  if (!cb) return undefined;
  return (grip, screen) => {
    if (
      grip.kind !== "mleader-arrow" &&
      grip.kind !== "mleader-vertex" &&
      grip.kind !== "mleader-landing" &&
      grip.kind !== "mleader-dogleg" &&
      grip.kind !== "mleader-text"
    )
      return;
    cb({
      entityId: grip.entityId,
      gripKind: grip.kind,
      gripPayload: grip.payload,
      gripWorld: grip.world,
      screen,
    });
  };
}
